import clsx from 'clsx';
import { mergeProps } from 'react-aria';

import { type ButtonHookProps, useButton } from '../Button';
import { type ImageProps, ProgressiveImage } from '../Image';
import { Card, filterCardDOMProps } from './Card';

export interface CardWrapperButtonProps
    extends
        Omit<ButtonHookProps, 'elementType'>,
        Pick<React.AriaAttributes, 'aria-haspopup' | 'aria-expanded' | 'aria-controls'> {
    /** Raster image rendered in the media slot. Ignored when `illustration` is set. */
    image?: Omit<ImageProps, 'rounded'>;
    /** Custom media content (e.g. a mid-fidelity illustration) rendered instead of `image`. */
    illustration?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}

export function CardWrapperButton({
    ref,
    ...props
}: CardWrapperButtonProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const { image, illustration, children, className, ...rest } = props;

    const { buttonProps } = useButton({ ...rest, elementType: 'div' });
    return (
        <div
            ref={ref}
            {...mergeProps(filterCardDOMProps(rest), buttonProps)}
            className={clsx(
                'group/outer-card-btn',
                'flex flex-col gap-3',
                'data-[focus-visible]:focus-ring',
                className
            )}
        >
            <Card
                kind="glass"
                className="relative flex overflow-hidden transition-shadow ease-in-out group-hover/outer-card-btn:shadow-elevated"
            >
                {illustration ??
                    (image ? (
                        <ProgressiveImage
                            {...image}
                            rounded={false}
                            className={clsx('w-full object-cover', image.className)}
                        />
                    ) : null)}
            </Card>
            <div>{children}</div>
        </div>
    );
}
